/**
 * useSamples — Hook for sample CRUD operations (demo module)
 */

import { useState, useCallback, useEffect } from "react";
import useApi from "./useApi";
import { endpoints } from "config/api";
import logger from "services/logger";

export default function useSamples({ autoFetch = false } = {}) {
  const { get, post, put, del } = useApi();
  const [samples, setSamples] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSamples = useCallback(
    async ({ limit = 50, offset = 0, status, createdBy } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (limit) params.set("limit", limit);
        if (offset) params.set("offset", offset);
        if (status) params.set("status", status);
        if (createdBy) params.set("createdBy", createdBy);

        const url = `${endpoints.samples.base}?${params.toString()}`;
        const data = await get(url);
        setSamples(data.samples || []);
        setTotal(data.total || 0);
        return data;
      } catch (err) {
        logger.error("[useSamples]", "Failed to fetch samples:", err);
        setError(err);
        return { samples: [], total: 0 };
      } finally {
        setLoading(false);
      }
    },
    [get]
  );

  const getSample = useCallback(
    async (id) => {
      try {
        const data = await get(endpoints.samples.byId(id));
        return data.sample;
      } catch (err) {
        logger.error("[useSamples]", `Failed to fetch sample ${id}:`, err);
        throw err;
      }
    },
    [get]
  );

  const createSample = useCallback(
    async (sampleData) => {
      try {
        const data = await post(endpoints.samples.base, sampleData);
        setSamples((prev) => [data.sample, ...prev]);
        setTotal((prev) => prev + 1);
        return data.sample;
      } catch (err) {
        logger.error("[useSamples]", "Failed to create sample:", err);
        throw err;
      }
    },
    [post]
  );

  const updateSample = useCallback(
    async (id, sampleData) => {
      try {
        const data = await put(endpoints.samples.byId(id), sampleData);
        setSamples((prev) => prev.map((s) => (s.id === id ? data.sample : s)));
        return data.sample;
      } catch (err) {
        logger.error("[useSamples]", `Failed to update sample ${id}:`, err);
        throw err;
      }
    },
    [put]
  );

  const deleteSample = useCallback(
    async (id) => {
      try {
        await del(endpoints.samples.byId(id));
        setSamples((prev) => prev.filter((s) => s.id !== id));
        setTotal((prev) => prev - 1);
      } catch (err) {
        logger.error("[useSamples]", `Failed to delete sample ${id}:`, err);
        throw err;
      }
    },
    [del]
  );

  useEffect(() => {
    if (autoFetch) {
      fetchSamples();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch]);

  return {
    samples,
    total,
    loading,
    error,
    fetchSamples,
    getSample,
    createSample,
    updateSample,
    deleteSample,
  };
}
